import { MERGE_INVALID_STATUSES, OCG_MERGE_BANNED_CLIENT_IDS } from '@/features/timeEntries/constants';
import { isDraftTimeEntry, isReadyToReleaseTimeEntry } from './timeEntryBulkActions';
import type {
    CheckedMap,
    ClientSummary,
    ExportColumn,
    TimeEntryLike,
    TimeEntrySummary,
    TimeEntrySummaryRow,
} from '@/features/timeEntries/types';

export const getSelectedItems = (items: TimeEntryLike[] = [], checkedMap: CheckedMap = {}) =>
    items.filter(item => item.id !== undefined && checkedMap[item.id]);

export const summarizeTimeEntries = (data: TimeEntryLike[] = []): TimeEntrySummary => {
    const clientMap = new Map<string | number, ClientSummary>();
    data.forEach(item => {
        if(item.status == 'discarded') return;
        const clientId = item.client_number || 'Client Unassigned';
        const clientName = item.client_name || clientId;
        const matterId = item.matter_number || 'Matter Unassigned';
        const matterName = item.matter_name || matterId;
        const duration = item.duration || 0;
        const isBillable = item.is_billable;

        if (!clientMap.has(clientId)) {
            clientMap.set(clientId, { type: "client", id: clientId, name: clientName, hrs: 0, billable_hrs: 0, matters: new Map() });
        }
        const client = clientMap.get(clientId);
        if (!client) return;
        client.hrs += duration;
        if (isBillable) client.billable_hrs += duration;

        if (!client.matters.has(matterId)) {
            client.matters.set(matterId, { type: "matter", id: matterId, name: matterName, hrs: 0, billable_hrs: 0, parentClientId: clientId });
        }
        const matter = client.matters.get(matterId);
        if (!matter) return;
        matter.hrs += duration;
        if (isBillable) matter.billable_hrs += duration;
    });

    const combinedData: TimeEntrySummaryRow[] = [];
    clientMap.forEach(client => {
        combinedData.push({ type: "client", id: client.id, name: client.name, hrs: client.hrs, billable_hrs: client.billable_hrs });
        client.matters.forEach(matter => combinedData.push(matter));
    });
    let hrs = 0, billable_hrs = 0, released_hrs = 0, ready_hrs = 0;
    data.forEach(item => {
        hrs += item.duration || 0;
        if (item.status === 'released') released_hrs += item.duration || 0;
        // Ready means accepted and still awaiting release, including the legacy
        // created/fetched + ready_to_release representation of accepted entries.
        if (
            !['released', 'releasing', 'ready2release', 'discarded'].includes(item.status || '') &&
            item.timeentry_group?.status !== 'drafted' &&
            (item.status === 'accepted' || item.timeentry_group?.status === 'accepted' ||
                isReadyToReleaseTimeEntry(item))
        ) ready_hrs += item.duration || 0;
        if (item.is_billable) billable_hrs += item.duration || 0;
    });

    return {hrs, billable_hrs, released_hrs, ready_hrs, combinedData};
};

export const getDestinationStatus = (action: number): string => {
    switch (action) {
        case 0:
            return 'Saved';
        case 1:
            return 'Pending';
        default:
            return '';

    }
};

export const getMiraStatus = (action: number): string => {
    switch (action) {
        case 2:
            return 'discarded';
        case 0:
        case 1:
        case 4:
        default:
            return 'ready2release';

    }
};

export const hasInvalidItemStatus = (items: TimeEntryLike[] = [], invalidStatus: string[] = []) =>
    items.some(item => item.status !== undefined && invalidStatus.includes(item.status));

export const isDeletableTimeEntry = (item: TimeEntryLike) =>
    ![item.status, item.timeentry_group?.status].some(status =>
        ['released', 'releasing', 'ready2release'].includes(String(status ?? '').trim().toLowerCase())
    );

export type MergeBillableMode = 'all' | 'billableOnly'

export const isBillableTimeEntry = (item?: TimeEntryLike | null) =>
    Boolean(item?.is_billable)

export const findEntriesByIds = (
    entries: TimeEntryLike[] = [],
    ids: Array<string | number> = [],
) => {
    const idSet = new Set(ids.map(id => String(id)))
    return entries.filter(item => item?.id != null && idSet.has(String(item.id)))
}

export const getMergeIdsForBillableMode = (
    entries: TimeEntryLike[] = [],
    mode: MergeBillableMode = 'all',
) => {
    const source = mode === 'billableOnly'
        ? entries.filter(isBillableTimeEntry)
        : entries
    return source
        .map(item => item.id)
        .filter((id): id is string | number => id !== undefined && id !== null)
}

export const canConfirmMergeBillableMode = (
    entries: TimeEntryLike[] = [],
    mode: MergeBillableMode = 'all',
) => getMergeIdsForBillableMode(entries, mode).length >= 2

const isDraftForMerge = (entry: TimeEntryLike) => {
    if (entry.status === 'accepted' || entry.timeentry_group?.status === 'accepted') return false;
    return isDraftTimeEntry(entry) || entry.status === 'draft' || entry.status === 'drafted';
};

// Merge is not approval: the API must keep an all-draft merge in its draft group.
export const getTimeEntryMergePayload = (
    entries: TimeEntryLike[],
    approvedBy?: string | number,
) => ({
    ids: getMergeIdsForBillableMode(entries),
    ...(entries.length > 0 && entries.every(isDraftForMerge) ? {} : {
        approved_by: approvedBy,
        approved_on: new Date().toISOString(),
    }),
});

export const isMergeActionInvalid = (items: TimeEntryLike[] = []) => {
    try {
        const settings = JSON.parse(sessionStorage.getItem('settings') || '[]');
        const FEATURE_OCG = settings.filter(s => s.name == 'FEATURE_OCG')[0];
        const hasBannedClient = items.some(item => OCG_MERGE_BANNED_CLIENT_IDS.includes(Number(item.client_id)));
        if (hasBannedClient && FEATURE_OCG && FEATURE_OCG.value.ENABLED) return true;
    } catch {
        // Keep merge validation available when optional session settings are missing or malformed.
    }

    const invalidStatuses = [...MERGE_INVALID_STATUSES, 'releasing'];
    const hasInvalidItems = items.some(item =>
        [item.status, item.timeentry_group?.status].some(status => status != null && invalidStatuses.includes(status))
    );
    if (hasInvalidItems) return true;

    // Draft matters are suggestions. Only accepted/legacy non-draft matters
    // constrain the merge. The API keeps High over Low, resolves Low candidates,
    // and uses the accepted matter when drafts merge into an accepted entry.
    const uniqueMatterNumbers = new Set(items.filter(item => !isDraftForMerge(item)).map(item => {
        const payload = item.timeentry_group?.metadata?.merge_payload ?? item;
        return payload.matter_number ?? payload.matter_id;
    }).filter(Boolean).map(String));
    if (uniqueMatterNumbers.size > 1) return true;
    if (items.length <= 1) return true;

    const normalizeDate = (d) => {
        const date = new Date(d);
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    };
    const uniqueDates = new Set(items.map(item => normalizeDate(item.date)));

    return uniqueDates.size > 1;
};

export const countSuggestGroups = (items: TimeEntryLike[] = []) => {
    const entries = items.filter(item =>
        (item.status === 'created' || item.status === 'fetched') &&
        item.sub_status !== 'ready_to_release'
    );

    const groups = new Set();
    let nullGroupCount = 0;

    entries.forEach(item => {
        if (item.group_id) {
            groups.add(item.group_id);
        } else {
            nullGroupCount += 1;
        }
    });

    return groups.size + nullGroupCount;
};

export const buildTimeEntryExportData = (
    items: TimeEntryLike[] = [],
    checkedItems: TimeEntryLike[] = [],
    exportColumns: ExportColumn[] = [],
) => {
    const sourceItems = checkedItems.length > 0 ? checkedItems : items;

    return sourceItems.map(item => {
        const newItem: Record<string, any> = {};
        exportColumns.forEach(col => {
            newItem[col.field] = col.format ? col.format(item) : item[col.field];
        });
        return newItem;
    });
};
